import { WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR } from "@/constants";
import { REVIEWS_WIDGET_NAMES } from "@/constants/widget-names";
import { globalProductIdManagerToken } from "@/tokens";
import { AverageReviewsWidgetDefinition } from "@apps/reviews/definitions";
import { ProductIdFromStaticProvider } from "@lib/ProductIdManager/ProductIdFromStaticProvider";
import { ProductIdManager } from "@lib/ProductIdManager/ProductIdManager";
import AnalyticsEventsCollectorInterface from "@modules/analytics_events_collector/AnalyticsEventsCollector/AnalyticsEventsCollectorInterface";
import { AnalyticsEventsFactory } from "@modules/analytics_events_collector/AnalyticsEventsCollector/AnalyticsEventsFactory";
import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
import WidgetInjectedSdkEvent from "@modules/sdk/common_sdk/events/WidgetInjectedSdkEvent";
import { dispatchSdkEvent } from "@modules/sdk/helpers";
import IntersectionObserverInjector from "@ui-utils/Injector/IntersectionObserverInjector";
import MutationObserverInjector from "@ui-utils/Injector/MutationObserverInjector";
import {
    parseIntDataAttr,
    parseIntFromGidDataAttr,
    parseOptionalBooleanDataAttr,
    parseStringDataAttr,
} from "@ui-utils/Injector/utils";
import { container } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";

const OLD_REVIEW_PRODUCT_ID = "product_id";
const REVIEW_PRODUCT_ID = "gw-product-id";
const ELEMENT_TO_SCROLL_ID = "gw-element-to-scroll-id";
const ELEMENT_TO_CLICK_ID = "gw-element-to-click-id";

class AverageReviewsWidgetInjector {
    private ioi: IntersectionObserverInjector;

    constructor(
        private readonly placeholderSelector: string,
        private readonly logger: LoggerInterface,
        private readonly eventsCollector: AnalyticsEventsCollectorInterface,
        private readonly analyticsEventsFactory: AnalyticsEventsFactory,
        private readonly dynamicImportManager: DynamicImportManager,
        private readonly moi: MutationObserverInjector
    ) {
        this.ioi = new IntersectionObserverInjector(
            IntersectionObserver,
            this.moi,
            this.placeholderSelector,
            {
                rootMargin: "0px 0px 50% 0px",
            }
        );
    }

    start(): void {
        this.logger.debug("AverageReviewsWidgetInjector start", {
            placeholderSelector: this.placeholderSelector,
        });

        this.ioi.targets$.subscribe((placeholder) => {
            this.logger?.debug(
                "AverageReviewsWidgetInjector found placeholder",
                {
                    placeholder,
                }
            );

            let productIdFromDataAttr =
                parseIntDataAttr(placeholder, REVIEW_PRODUCT_ID) ||
                parseIntFromGidDataAttr(placeholder, REVIEW_PRODUCT_ID);
            if (productIdFromDataAttr === null) {
                productIdFromDataAttr = parseIntDataAttr(
                    placeholder,
                    OLD_REVIEW_PRODUCT_ID
                );

                if (productIdFromDataAttr !== null) {
                    this.eventsCollector.pushEvent(
                        this.analyticsEventsFactory.createWidgetInjectedWithAttribute(
                            REVIEWS_WIDGET_NAMES.AVERAGE_REVIEWS_WIDGET,
                            OLD_REVIEW_PRODUCT_ID
                        )
                    );
                }
            }

            const elementToScrollId = parseStringDataAttr(
                placeholder,
                ELEMENT_TO_SCROLL_ID
            );
            const elementToClickId = parseStringDataAttr(
                placeholder,
                ELEMENT_TO_CLICK_ID
            );

            if (productIdFromDataAttr === null) {
                this.logger.error(
                    `AverageReviewsWidget placeholder does not have data-${REVIEW_PRODUCT_ID} attribute.`
                );
            }

            const productIdManager =
                productIdFromDataAttr === null
                    ? container.resolve(globalProductIdManagerToken)
                    : new ProductIdManager();

            if (productIdFromDataAttr !== null) {
                productIdManager.registerProvider(
                    new ProductIdFromStaticProvider(productIdFromDataAttr)
                );
            }

            void (async () => {
                const AverageReviewsWidget =
                    await this.dynamicImportManager.importDefault(
                        AverageReviewsWidgetDefinition,
                        true
                    );

                const widget = new AverageReviewsWidget(
                    {
                        target: placeholder,
                        isWidgetShadowRootEnabled: parseOptionalBooleanDataAttr(
                            placeholder,
                            WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR
                        ),
                        elementToScrollId: elementToScrollId ?? "",
                        elementToClickId: elementToClickId ?? "",
                    },
                    this.logger,
                    productIdManager
                );

                await widget.init();

                this.eventsCollector.pushEvent(
                    this.analyticsEventsFactory.createWidgetInjected(
                        REVIEWS_WIDGET_NAMES.AVERAGE_REVIEWS_WIDGET
                    )
                );

                dispatchSdkEvent(
                    new WidgetInjectedSdkEvent(
                        REVIEWS_WIDGET_NAMES.AVERAGE_REVIEWS_WIDGET
                    )
                );
            })();
        });

        this.ioi.start();
    }
}

export default AverageReviewsWidgetInjector;
